import { Injectable, Injector, inject } from '@angular/core';
import { BereichStoreService, DB_CONNECTION_CONFIGS, OfflineModeService, PROJECT_PROVIDER } from '@luzula/luzula-lib';
import { DatabaseConfigService } from './database-config.service';

/**
 * bpweb-Konfiguration des Bereiche-Stores (dedizierte DB `bpweb-bereiche`,
 * ein Doc je Bereich mit stabiler ID). Lifecycle/CRUD/Sync kommen aus
 * {@link BereichStoreService}; hier nur Remote-URL (inkl. Credentials) und
 * Name der lokalen Replik. (Muster: ZeichnungenService.)
 */
@Injectable({ providedIn: 'root' })
export class BereicheService extends BereichStoreService {
	private dbConfig = inject(DatabaseConfigService);
	private dbConnectionConfigs = inject(DB_CONNECTION_CONFIGS);
	private injector = inject(Injector);

	/**
	 * Gruppen des Projekts (feature/user-groups): neue/geaenderte Bereich-Docs erben additiv die
	 * groups des Projekts. Lazy ueber PROJECT_PROVIDER (Injektions-Kreis vermeiden). Nur wenn das
	 * aktuelle Projekt-Doc zur projectId passt (sonst keine Stempelung).
	 */
	protected override groupsForProject(projectId: string): string[] | undefined {
		const ps = this.injector.get(PROJECT_PROVIDER, null) as { currentProjectDoc?: () => { _id?: string; groups?: string[] } | null } | null;
		const doc = ps?.currentProjectDoc?.();
		return doc && doc._id === projectId ? doc.groups : undefined;
	}

	/**
	 * Offline gestellt? Dann schiebt der Speicher nichts von selbst zum Server;
	 * die Bereiche warten auf den Abgleich, der sie seit 26.09.2026 auch
	 * mitnimmt (dieselbe Lücke wie bei den Zeichnungen, Nutzer-Befund
	 * 25.09.2026). Erst im Moment der Frage geholt, nicht beim Bauen.
	 */
	protected override imOfflineModus(): boolean {
		return this.injector.get(OfflineModeService).userOfflineMode();
	}

	/** Remote-Master = `bpweb-bereiche` (nur online). */
	protected get remoteDbUrl(): string | null {
		if (!navigator.onLine) return null;
		const conn = this.dbConnectionConfigs[0];
		return conn ? conn.url(this.dbConfig.bereicheDbName) : null;
	}

	/** Lokale Replik (konstanter Name). */
	protected get localCacheDbName(): string {
		return `${this.dbConfig.bereicheDbName}-cache`;
	}

	/** Gateway-Auth: Bearer-Header pro Request für die Remote-Replikation. */
	protected override authHeaders(): Record<string, string> | null | undefined {
		return this.dbConnectionConfigs[0]?.getAuthHeaders?.() ?? null;
	}

	/**
	 * Abgelaufener Token: einmal still erneuern, dann wiederholt der fetch-Wrapper
	 * die Anfrage. Ohne das blieb die Replikation nach Token-Ablauf bei 401 stehen,
	 * bis die Seite neu geladen wurde.
	 */
	protected override authRefresh(): Promise<boolean> {
		return this.dbConnectionConfigs[0]?.refreshAuth?.() ?? Promise.resolve(false);
	}

	/**
	 * Auf dem Nur-Lese-Weg festgefahren: zurueck auf die richtige Adresse.
	 *
	 * Die Verbindung waehlt ihren Weg nur beim Aufbau. Startet die App ohne
	 * greifbaren Token (Standby, wackliges Netz), bleibt sie auf
	 * `/db/public/...` stehen — Ansehen geht, Speichern bekommt ein 405, und
	 * das fuer die ganze Sitzung (Nutzer-Befund 01.09.2026). bpweb laeuft im
	 * Gelaende auf Mobilgeraeten, also genau dort, wo das passiert.
	 */
	protected override adressReparatur(url: string): string | undefined {
		return this.dbConnectionConfigs[0]?.repariereAdresse?.(url);
	}
}
